
// =============================================================================
// FILE: packages/shared/src/constants/ipc.constants.ts
// LAYER: Foundation — IPC Channel Registry
// PURPOSE: Every IPC channel name used between renderer and main process.
//          Renderer: window.api.invoke(IPC.ASSET_CREATE, dto)
//          Main:     ipcMain.handle(IPC.ASSET_CREATE, handler)
//          Never use raw strings for IPC channels.
// =============================================================================

export const IPC = {
  // ---------------------------------------------------------------------------
  // Auth
  // ---------------------------------------------------------------------------
  AUTH_LOGIN:               'auth:login',
  AUTH_LOGIN_AZURE:         'auth:loginAzure',
  AUTH_ENTRA_DEVICE_BEGIN:  'auth:entra:deviceBegin',
  AUTH_ENTRA_DEVICE_AWAIT:  'auth:entra:deviceAwait',
  AUTH_ENTRA_STATUS:        'auth:entra:status',
  AUTH_LOGOUT:              'auth:logout',
  AUTH_REFRESH_TOKEN:       'auth:refreshToken',
  AUTH_GET_SESSION:         'auth:getSession',
  AUTH_CHANGE_PASSWORD:     'auth:changePassword',

  // ---------------------------------------------------------------------------
  // Users
  // ---------------------------------------------------------------------------
  USER_LIST:                'user:list',
  USER_GET:                 'user:get',
  USER_CREATE:              'user:create',
  USER_UPDATE:              'user:update',
  USER_DEACTIVATE:          'user:deactivate',
  USER_ASSIGN_ROLE:         'user:assignRole',
  USER_REVOKE_ROLE:         'user:revokeRole',

  // ---------------------------------------------------------------------------
  // Roles & Permissions
  // ---------------------------------------------------------------------------
  ROLE_LIST:                'role:list',
  ROLE_CREATE:              'role:create',
  ROLE_UPDATE:              'role:update',
  ROLE_DELETE:              'role:delete',
  PERMISSION_LIST:          'permission:list',

  // ---------------------------------------------------------------------------
  // Assets
  // ---------------------------------------------------------------------------
  ASSET_LIST:               'asset:list',
  ASSET_GET:                'asset:get',
  ASSET_CREATE:             'asset:create',
  ASSET_UPDATE:             'asset:update',
  ASSET_DELETE:             'asset:delete',
  ASSET_SEARCH:             'asset:search',
  ASSET_IMPORT_IFC:         'asset:importIfc',

  // ---------------------------------------------------------------------------
  // Buildings
  // ---------------------------------------------------------------------------
  BUILDING_LIST:            'building:list',
  BUILDING_GET:             'building:get',
  BUILDING_GET_TREE:        'building:getTree',
  BUILDING_CREATE:          'building:create',
  BUILDING_UPDATE:          'building:update',
  BUILDING_ARCHIVE:         'building:archive',

  // ---------------------------------------------------------------------------
  // Floors
  // ---------------------------------------------------------------------------
  FLOOR_LIST:               'floor:list',
  FLOOR_GET:                'floor:get',
  FLOOR_CREATE:             'floor:create',
  FLOOR_UPDATE:             'floor:update',

  // ---------------------------------------------------------------------------
  // Rooms
  // ---------------------------------------------------------------------------
  ROOM_LIST:                'room:list',
  ROOM_GET:                 'room:get',
  ROOM_CREATE:              'room:create',
  ROOM_UPDATE:              'room:update',

  // ---------------------------------------------------------------------------
  // Telecom Spaces
  // ---------------------------------------------------------------------------
  TELECOM_LIST:             'telecom:list',
  TELECOM_GET:              'telecom:get',
  TELECOM_CREATE:           'telecom:create',
  TELECOM_UPDATE:           'telecom:update',

  // ---------------------------------------------------------------------------
  // Racks
  // ---------------------------------------------------------------------------
  RACK_LIST:                'rack:list',
  RACK_GET:                 'rack:get',
  RACK_GET_ELEVATION:       'rack:getElevation',
  RACK_CREATE:              'rack:create',
  RACK_UPDATE:              'rack:update',
  RACK_ASSIGN_UNIT:         'rack:assignUnit',
  RACK_CLEAR_UNIT:          'rack:clearUnit',

  // ---------------------------------------------------------------------------
  // Patch Panels & Switches
  // ---------------------------------------------------------------------------
  PATCH_PANEL_LIST:         'patchPanel:list',
  PATCH_PANEL_CREATE:       'patchPanel:create',
  PATCH_PANEL_UPDATE:       'patchPanel:update',
  SWITCH_LIST:              'switch:list',
  SWITCH_CREATE:            'switch:create',
  SWITCH_UPDATE:            'switch:update',

  // ---------------------------------------------------------------------------
  // Cables
  // ---------------------------------------------------------------------------
  CABLE_LIST:               'cable:list',
  CABLE_GET:                'cable:get',
  CABLE_TRACE:              'cable:trace',
  CABLE_CREATE:             'cable:create',
  CABLE_UPDATE:             'cable:update',
  CABLE_DELETE:             'cable:delete',
  CABLE_PATH_LIST:          'cablePath:list',
  CABLE_PATH_CREATE:        'cablePath:create',

  // ---------------------------------------------------------------------------
  // Projects
  // ---------------------------------------------------------------------------
  PROJECT_LIST:             'project:list',
  PROJECT_GET:              'project:get',
  PROJECT_CREATE:           'project:create',
  PROJECT_UPDATE:           'project:update',
  PROJECT_ARCHIVE:          'project:archive',
  // §4 Project lifecycle states
  PROJECT_ARCHIVE_TILE:     'project:archiveTile',
  PROJECT_RESTORE:          'project:restore',
  PROJECT_SOFT_DELETE:      'project:softDelete',
  PROJECT_PURGE:            'project:purge',

  // ---------------------------------------------------------------------------
  // Floor / Room / TelecomSpace
  // ---------------------------------------------------------------------------
  FLOOR_LIST_BY_BUILDING:   'floor:listByBuilding',
  FLOOR_ARCHIVE:            'floor:archive',

  ROOM_LIST_BY_FLOOR:       'room:listByFloor',
  ROOM_ARCHIVE:             'room:archive',

  TELECOM_LIST_BY_ROOM:     'telecom:listByRoom',
  TELECOM_LIST_BY_TYPE:     'telecom:listByType',
  TELECOM_ARCHIVE:          'telecom:archive',

  // ---------------------------------------------------------------------------
  // IFC Parsing
  // ---------------------------------------------------------------------------
  IFC_PARSE:                'ifc:parse',
  IFC_PARSE_STREAM:         'ifc:parseStream',

  // ---------------------------------------------------------------------------
  // Clash Detection
  // ---------------------------------------------------------------------------
  CLASH_TEST_LIST:          'clash:testList',
  CLASH_TEST_GET:           'clash:testGet',
  CLASH_TEST_CREATE:        'clash:testCreate',
  CLASH_TEST_RUN:           'clash:testRun',
  CLASH_RESULT_LIST:        'clash:resultList',
  CLASH_RESULT_RESOLVE:     'clash:resultResolve',

  // ---------------------------------------------------------------------------
  // Documents
  // ---------------------------------------------------------------------------
  DOCUMENT_LIST:            'document:list',
  DOCUMENT_GET:             'document:get',
  DOCUMENT_UPLOAD:          'document:upload',
  DOCUMENT_UPDATE:          'document:update',
  DOCUMENT_DOWNLOAD:        'document:download',
  DOCUMENT_DELETE:          'document:delete',

  // ---------------------------------------------------------------------------
  // Drawings
  // ---------------------------------------------------------------------------
  DRAWING_LIST:             'drawing:list',
  DRAWING_CREATE:           'drawing:create',
  DRAWING_UPDATE:           'drawing:update',

  // ---------------------------------------------------------------------------
  // Tasks
  // ---------------------------------------------------------------------------
  TASK_LIST:                'task:list',
  TASK_GET:                 'task:get',
  TASK_CREATE:              'task:create',
  TASK_UPDATE:              'task:update',
  TASK_COMPLETE:            'task:complete',
  TASK_ASSIGN:              'task:assign',

  // ---------------------------------------------------------------------------
  // Sync
  // ---------------------------------------------------------------------------
  SYNC_TRIGGER:             'sync:trigger',
  SYNC_STATUS:              'sync:status',
  SYNC_QUEUE_LIST:          'sync:queueList',
  SYNC_HISTORY_LIST:        'sync:historyList',
  SYNC_CONFLICT_LIST:       'sync:conflictList',
  SYNC_RESOLVE_CONFLICT:    'sync:resolveConflict',

  // ---------------------------------------------------------------------------
  // SharePoint
  // ---------------------------------------------------------------------------
  SHAREPOINT_BROWSE:        'sharepoint:browse',
  SHAREPOINT_UPLOAD:        'sharepoint:upload',
  SHAREPOINT_DOWNLOAD:      'sharepoint:download',
  SHAREPOINT_CONNECT:       'sharepoint:connect',

  // ---------------------------------------------------------------------------
  // Revit
  // ---------------------------------------------------------------------------
  REVIT_CONNECT:            'revit:connect',
  REVIT_EXPORT_IFC:         'revit:exportIfc',
  REVIT_SYNC_ELEMENTS:      'revit:syncElements',
  REVIT_GET_CLASHES:        'revit:getClashes',

  // ---------------------------------------------------------------------------
  // AutoCAD
  // ---------------------------------------------------------------------------
  AUTOCAD_CONNECT:          'autocad:connect',
  AUTOCAD_IMPORT_LAYERS:    'autocad:importLayers',
  AUTOCAD_IMPORT_CABLES:    'autocad:importCables',

  // ---------------------------------------------------------------------------
  // APS (Autodesk Platform Services)
  // ---------------------------------------------------------------------------
  APS_GET_MODELS:           'aps:getModels',
  APS_GET_VIEWER_TOKEN:     'aps:getViewerToken',
  APS_UPLOAD_MODEL:         'aps:uploadModel',

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------
  SEARCH_GLOBAL:            'search:global',
  SEARCH_ASSETS:            'search:assets',

  // ---------------------------------------------------------------------------
  // Activity Log
  // ---------------------------------------------------------------------------
  ACTIVITY_LIST:            'activity:list',

  // ---------------------------------------------------------------------------
  // Audit Trail
  // ---------------------------------------------------------------------------
  AUDIT_LIST:               'audit:list',

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------
  NOTIFICATION_LIST:        'notification:list',
  NOTIFICATION_MARK_READ:   'notification:markRead',
  NOTIFICATION_MARK_ALL_READ: 'notification:markAllRead',

  // ---------------------------------------------------------------------------
  // Digital Twin
  // ---------------------------------------------------------------------------
  TWIN_GET_STATE:           'twin:getState',
  TWIN_GET_ASSET_STATE:     'twin:getAssetState',
  TWIN_LIST_SNAPSHOTS:      'twin:listSnapshots',
  TWIN_GET_SNAPSHOT:        'twin:getSnapshot',

  // ---------------------------------------------------------------------------
  // AI / Embeddings
  // ---------------------------------------------------------------------------
  AI_SEARCH:                'ai:search',
  AI_ASK:                   'ai:ask',
  AI_CHAT:                  'ai:chat',
  AI_CHAT_STREAM:           'ai:chat:stream',
  AI_LIST_PROVIDERS:        'ai:listProviders',
  AI_GET_DEFAULT_PROVIDER:  'ai:getDefaultProvider',
  AI_SET_DEFAULT_PROVIDER:  'ai:setDefaultProvider',

  // ---------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------
  SETTINGS_GET:             'settings:get',
  SETTINGS_SET:             'settings:set',
  SETTINGS_LIST:            'settings:list',

  // ---------------------------------------------------------------------------
  // Pilot Demo Mode — Administrator-only DB reset to enterprise demo dataset
  // ---------------------------------------------------------------------------
  PILOT_DEMO_RESET:         'pilotDemo:reset',
  PILOT_DEMO_STATUS:        'pilotDemo:status',

  // ---------------------------------------------------------------------------
  // Hard Reset — administrator-only true wipe. Deletes every user-domain row
  // (projects, buildings, racks, assets, documents, audit history) and never
  // re-seeds demo data, regardless of the USE_DEMO_SEED env flag. The only
  // rows that survive are the canonical RBAC seed + the local admin user
  // (required for login). Returns the same diagnostics shape as
  // PILOT_DEMO_RESET so the UI can reuse the existing banners.
  // ---------------------------------------------------------------------------
  SETTINGS_HARD_RESET:      'settings:hardReset',

  // ---------------------------------------------------------------------------
  // Database Inspector — read-only diagnostics for Settings → Diagnostics.
  // Surfaces the absolute SQLite path, file size, mtime, SQLite version and
  // user-domain row counts so issues like "Reset didn't actually delete" or
  // "the app is reading a different DB" are visible at a glance.
  // ---------------------------------------------------------------------------
  DIAGNOSTICS_DB_INFO:      'diagnostics:dbInfo',

  // ---------------------------------------------------------------------------
  // Dashboard — aggregate live counts of canonical entities
  // ---------------------------------------------------------------------------
  DASHBOARD_STATS:          'dashboard:stats',
  DASHBOARD_RECENT_ACTIVITY: 'dashboard:recentActivity',

  // ---------------------------------------------------------------------------
  // Cabling — port-level connectivity graph (Sprint 3)
  // ---------------------------------------------------------------------------
  CABLING_LIST_OUTLETS:         'cabling:listOutlets',
  CABLING_LIST_PORTS_BY_PARENT: 'cabling:listPortsByParent',
  CABLING_LIST_PARENTS:         'cabling:listParents',
  CABLING_FIND_FREE_PORT:       'cabling:findFreePort',

  // Revit Add-In bridge (Sprint 4 — Settings UI controls the local bridge)
  REVIT_BRIDGE_STATUS:          'revit:bridge:status',
  REVIT_BRIDGE_MINT_PAIR_CODE:  'revit:bridge:mintPairCode',
  REVIT_BRIDGE_LIST_TOKENS:     'revit:bridge:listTokens',
  REVIT_BRIDGE_REVOKE_TOKEN:    'revit:bridge:revokeToken',
  CABLING_TRACE:                'cabling:trace',
  CABLING_CONNECT:              'cabling:connect',
  CABLING_DISCONNECT:           'cabling:disconnect',
  CABLING_SET_STATUS:           'cabling:setStatus',

  // ---------------------------------------------------------------------------
  // Track A — A2 Document Ingestion / KB search (Seam #2 surface)
  // ---------------------------------------------------------------------------
  DOCUMENT_INGEST:              'document:ingest',
  KB_SEARCH:                    'kb:search',

  // ---------------------------------------------------------------------------
  // Track B — B2 Task Orchestration
  // ---------------------------------------------------------------------------
  TASKS_EXPAND:                 'tasks:expand',
  TASKS_LIST:                   'tasks:list',

  // ---------------------------------------------------------------------------
  // Track B — B3 Project Coach (read-only summary over Seam #1 + Seam #2)
  // ---------------------------------------------------------------------------
  COACH_SUMMARISE:              'coach:summarise',
  COACH_SCORECARD:              'coach:scorecard',

  // ---------------------------------------------------------------------------
  // Track B — B4 Suggest-Mode Email (P1-03)
  // ---------------------------------------------------------------------------
  EMAIL_LIST_TRIGGERS:          'email:listTriggers',
  EMAIL_SET_TRIGGER_ENABLED:    'email:setTriggerEnabled',
  EMAIL_GET_KILL_SWITCH:        'email:getKillSwitch',
  EMAIL_SET_KILL_SWITCH:        'email:setKillSwitch',
  EMAIL_RUN_TRIGGER:            'email:runTrigger',
  EMAIL_LIST_DRAFTS:            'email:listDrafts',
  EMAIL_GET_DRAFT:              'email:getDraft',
  EMAIL_UPDATE_DRAFT:           'email:updateDraft',
  EMAIL_APPROVE_AND_SEND:       'email:approveAndSend',
  EMAIL_DISCARD_DRAFT:          'email:discardDraft',

  // ---------------------------------------------------------------------------
  // Track B — B5 Project Assistant / Autopilot (P1-04 Phase 2)
  // Plan-only surface. Execution lives behind a future channel + approval
  // scope grant — never on these read/approve channels.
  // ---------------------------------------------------------------------------
  AUTOPILOT_PLAN_CREATE:        'autopilot:plan.create',
  AUTOPILOT_PLAN_PREVIEW:       'autopilot:plan.preview',
  AUTOPILOT_PLAN_LIST:          'autopilot:plan.list',
  AUTOPILOT_PLAN_APPROVE:       'autopilot:plan.approve',
  AUTOPILOT_PLAN_REJECT:        'autopilot:plan.reject',

  // ---------------------------------------------------------------------------
  // Track B — B6 AI Agent Projects (P1-05)
  // The agent is a B5 client; it composes capability ports, never adds new
  // side-effects of its own.
  // ---------------------------------------------------------------------------
  AGENT_LIST_TEMPLATES:         'agent:listTemplates',
  AGENT_LIST_GOALS:             'agent:listGoals',
  AGENT_GET_GOAL:               'agent:getGoal',
  AGENT_REGISTER_GOAL:          'agent:registerGoal',
  AGENT_APPROVE_AND_RUN:        'agent:approveAndRun',
  AGENT_CANCEL_GOAL:            'agent:cancelGoal',

  // ---------------------------------------------------------------------------
  // Track A/B — B8 Company Cost Templates (P1-06)
  // ---------------------------------------------------------------------------
  COST_LIST_LINEAGES:           'cost:listLineages',
  COST_LIST_VERSIONS:           'cost:listVersions',
  COST_GET_TEMPLATE:            'cost:getTemplate',
  COST_GET_BOUND:               'cost:getBoundTemplate',
  COST_IMPORT_JSON:             'cost:importJson',
  COST_BIND_PROJECT:            'cost:bindProject',
  COST_UNBIND_PROJECT:          'cost:unbindProject',
  COST_ESTIMATE_ON_TEMPLATE:    'cost:estimateOnTemplate',
  COST_ESTIMATE_FOR_PROJECT:    'cost:estimateForProject',
  COST_LIST_ESTIMATES:          'cost:listEstimates',
  COST_SUGGEST_SCOPE:           'cost:suggestScope',
  COST_GET_ACTIVE_BINDING:      'cost:getActiveBinding',
  COST_LIST_BINDING_HISTORY:    'cost:listBindingHistory',

  // ---------------------------------------------------------------------------
  // Track A/B — B9 Automated Backup & DR (P1-07)
  // ---------------------------------------------------------------------------
  BACKUP_LIST_DESTINATIONS:     'backup:listDestinations',
  BACKUP_LIST_SCHEDULES:        'backup:listSchedules',
  BACKUP_UPSERT_SCHEDULE:       'backup:upsertSchedule',
  BACKUP_DELETE_SCHEDULE:       'backup:deleteSchedule',
  BACKUP_RUN_MANUAL:            'backup:runManual',
  BACKUP_LIST_SNAPSHOTS:        'backup:listSnapshots',
  BACKUP_VERIFY_SNAPSHOT:       'backup:verifySnapshot',
  BACKUP_RESTORE_SNAPSHOT:      'backup:restoreSnapshot',
  BACKUP_LIST_EVENTS:           'backup:listEvents',
  BACKUP_HEALTH:                'backup:health',
  BACKUP_SCHEDULER_STATUS:      'backup:schedulerStatus',

  // ---------------------------------------------------------------------------
  // Track A/B — B7 Multilingual AI (P1-08)
  // ---------------------------------------------------------------------------
  LOCALE_LIST:                  'locale:list',
  LOCALE_GET_PREFERENCE:        'locale:getPreference',
  LOCALE_SET_PREFERENCE:        'locale:setPreference',
  LOCALE_TRANSLATE:             'locale:translate',
  LOCALE_PROVIDER_INFO:         'locale:providerInfo',

  // ---------------------------------------------------------------------------
  // Track A/B — Production Hardening: P1.4 Health Monitoring
  // ---------------------------------------------------------------------------
  SYSTEM_HEALTH:                'system:health',

  // ---------------------------------------------------------------------------
  // Standalone Portable Application — Phase 2 foundation
  //   - SYSTEM_PORTABLE_INFO:   read-only descriptor of the portable root
  //                             and resolved subpaths.
  //   - SYSTEM_RESET_WORKSPACE: admin-only volatile-state wipe (cache, tmp,
  //                             sessions, rotated logs, MSAL cache, ingestion
  //                             scratch). Never touches DB / attachments /
  //                             backups / templates / projects / config.
  // ---------------------------------------------------------------------------
  SYSTEM_PORTABLE_INFO:         'system:portableInfo',
  SYSTEM_RESET_WORKSPACE:       'system:resetWorkspace',

  // ---------------------------------------------------------------------------
  // §7 Cloud surface — Google Drive (STUB / NOT_CONFIGURED) + Emergent-hosted
  // remote access (STUB / disabled). All channels always succeed but return
  // NOT_CONFIGURED / enabled=false until credentials land.
  // ---------------------------------------------------------------------------
  CLOUD_REMOTE_ACCESS_STATUS:   'cloud:remoteAccessStatus',
  CLOUD_GDRIVE_STATUS:          'cloud:gdriveStatus',
  CLOUD_GDRIVE_OAUTH_BEGIN:     'cloud:gdriveOAuthBegin',
  CLOUD_GDRIVE_SYNC:            'cloud:gdriveSync',

  // ---------------------------------------------------------------------------
  // Document Control Engine — source of truth above Ingestion + below UI.
  //   - DOCREG_LIST:            list registry rows by project
  //   - DOCREG_REVISIONS:       revision history for a single logicalKey
  //   - DOCREG_INGEST:          ingest a file (caller-supplied source path)
  //   - DOCREG_GET:             read one row
  //   - DOCREG_EXPORT_SNAPSHOT: build + persist read-only client snapshot
  //   - DOCREG_LAYOUT:          read-only pure descriptor of the on-disk tree
  // ---------------------------------------------------------------------------
  DOCREG_LIST:                  'docregistry:list',
  DOCREG_REVISIONS:             'docregistry:revisions',
  DOCREG_INGEST:                'docregistry:ingest',
  DOCREG_GET:                   'docregistry:get',
  DOCREG_EXPORT_SNAPSHOT:       'docregistry:exportSnapshot',
  DOCREG_LAYOUT:                'docregistry:layout',

  // ---------------------------------------------------------------------------
  // Instance Binding (Client Isolation + Licensing Boundary §6).
  // ---------------------------------------------------------------------------
  INSTANCE_GET:                 'instance:get',
  INSTANCE_SET_GMAIL:           'instance:setGmail',

  // ---------------------------------------------------------------------------
  // Post-portability stabilisation — boot-time IPC handler registration probe.
  // Renderer calls this once on app mount; main returns which expected channels
  // have a handler registered. The UI then shows "System Not Fully Initialised"
  // if any are missing.
  // ---------------------------------------------------------------------------
  SYSTEM_BOOT_PROBE:            'system:bootProbe',

  // ---------------------------------------------------------------------------
  // Track A/B — Production Hardening: P1.3 Automatic Migrations + Safe Rollback
  // ---------------------------------------------------------------------------
  MIGRATIONS_STATUS:            'migrations:status',
  MIGRATIONS_LIST_SNAPSHOTS:    'migrations:listSnapshots',
  MIGRATIONS_APPLY_PENDING:     'migrations:applyPending',
  MIGRATIONS_RUN_SCORECARD:     'migrations:runScorecard',

  // ---------------------------------------------------------------------------
  // Track A/B — Production Hardening: P1.5 Logging
  // ---------------------------------------------------------------------------
  LOGS_RECENT:                  'logs:recent',
  LOGS_SET_LEVEL:               'logs:setLevel',
  LOGS_GET_LEVEL:               'logs:getLevel',

  // ---------------------------------------------------------------------------
  // Track A/B — Production Hardening: P1.6 Performance Profiling
  // ---------------------------------------------------------------------------
  METRICS_SNAPSHOT:             'metrics:snapshot',
  METRICS_RESET:                'metrics:reset',

  // ---------------------------------------------------------------------------
  // B-FIELD.1 — Field Visit Workflow (Session Generation & Manifest Export)
  // ---------------------------------------------------------------------------
  FIELD_CREATE_SESSION:         'field:createSession',
  FIELD_LIST_SESSIONS:          'field:listSessions',
  FIELD_LIST_FOR_PARENT:        'field:listForParent',
  FIELD_GET_SESSION:            'field:getSession',
  FIELD_READ_MANIFEST:          'field:readManifest',
  FIELD_UPDATE_STATUS:          'field:updateStatus',

  // ---------------------------------------------------------------------------
  // B-FIELD.2 — Field Capture Core (photos / notes / measurements /
  // scans / issues / voice / markup + per-session checklist)
  // ---------------------------------------------------------------------------
  FIELD_ADD_NOTE:               'field:addNote',
  FIELD_ADD_PHOTO:              'field:addPhoto',
  FIELD_ADD_MEASUREMENT:        'field:addMeasurement',
  FIELD_ADD_SCAN:               'field:addScan',
  FIELD_ADD_ISSUE:              'field:addIssue',
  FIELD_LIST_CAPTURES:          'field:listCaptures',
  FIELD_COUNT_CAPTURES:         'field:countCaptures',
  FIELD_READ_CAPTURE_BINARY:    'field:readCaptureBinary',
  FIELD_DELETE_CAPTURE:         'field:deleteCapture',
  FIELD_CREATE_CHECKLIST_ITEM:  'field:createChecklistItem',
  FIELD_CREATE_CHECKLIST_BATCH: 'field:createChecklistBatch',
  FIELD_LIST_CHECKLIST:         'field:listChecklist',
  FIELD_SET_CHECKLIST_STATUS:   'field:setChecklistStatus',

  // ---------------------------------------------------------------------------
  // GCPE.1 — Global Change Propagation Engine
  // ---------------------------------------------------------------------------
  PROPAGATION_PROPOSE:             'propagation:propose',
  PROPAGATION_APPROVE:             'propagation:approve',
  PROPAGATION_RUN:                 'propagation:run',
  PROPAGATION_LIST_EVENTS:         'propagation:listEvents',
  PROPAGATION_LIST_EMISSIONS:      'propagation:listEmissions',
  PROPAGATION_LIST_BY_CORRELATION: 'propagation:listByCorrelation',
  PROPAGATION_VERIFY_CHAIN:        'propagation:verifyChain',
  PROPAGATION_LIST_EMITTERS:       'propagation:listEmitters',

  // ---------------------------------------------------------------------------
  // QAQC.1 — Validation gate (Phase 1)
  // ---------------------------------------------------------------------------
  QAQC_RUN_REPORT:                 'qaqc:runReport',
  QAQC_ACKNOWLEDGE:                'qaqc:acknowledge',
  QAQC_LIST_RULES:                 'qaqc:listRules',

  // ---------------------------------------------------------------------------
  // Desktop UX — Always-on-Top toggle + window state persistence
  // ---------------------------------------------------------------------------
  SYSTEM_SET_ALWAYS_ON_TOP:     'system:setAlwaysOnTop',
  SYSTEM_GET_ALWAYS_ON_TOP:     'system:getAlwaysOnTop',

  // ---------------------------------------------------------------------------
  // Help System — opens bundled documentation via the main process so the
  // sandboxed renderer never touches the file system directly. The handler
  // resolves the requested doc name against a whitelist mounted under
  // <resources>/docs/ (mirrored at the portable root by extraFiles).
  // SYSTEM_INFO returns the consolidated "where does my data live" descriptor
  // used by the Help → Migration & System Info screen.
  // ---------------------------------------------------------------------------
  HELP_LIST_DOCS:               'help:listDocs',
  HELP_OPEN_DOC:                'help:openDoc',
  SYSTEM_INFO:                  'system:info',

  // ---------------------------------------------------------------------------
  // Folder Monitor — Sync Engine watcher for Revit / AutoCAD / PDF / field
  // deliverables. Live events stream over PUSH_FOLDER_MONITOR_EVENT.
  // ---------------------------------------------------------------------------
  FOLDER_MONITOR_SELECT:        'folderMonitor:selectFolder',
  FOLDER_MONITOR_SET:           'folderMonitor:setFolder',
  FOLDER_MONITOR_START:         'folderMonitor:start',
  FOLDER_MONITOR_STOP:          'folderMonitor:stop',
  FOLDER_MONITOR_STATUS:        'folderMonitor:status',
  FOLDER_MONITOR_LIST_EVENTS:   'folderMonitor:listEvents',
  FOLDER_MONITOR_CLEAR_EVENTS:  'folderMonitor:clearEvents',
  FOLDER_MONITOR_OPEN_IN_FS:    'folderMonitor:openInFs',
  FOLDER_MONITOR_RUN_DIAGNOSTIC:'folderMonitor:runDiagnostic',
  PUSH_FOLDER_MONITOR_EVENT:    'push:folderMonitorEvent',

  // ---------------------------------------------------------------------------
  // Project Live Notes / Call Log (append-only per-project timeline)
  // ---------------------------------------------------------------------------
  PROJECT_LOG_ADD:              'project:log:add',
  PROJECT_LOG_LIST:             'project:log:list',
  PROJECT_LOG_DELETE:           'project:log:delete',
  PROJECT_LOG_CLEAR:            'project:log:clear',
  PROJECT_LOG_SUMMARY:          'project:log:summary',

  // ---------------------------------------------------------------------------
  // Meeting Capture Engine (B10 business layer, Cut B):
  //   live audio recording + timestamp-aligned typed notes + in-app player.
  // The audio blob (.webm/opus) is delivered to the renderer over the
  // MEETING_READ_AUDIO channel as base64, never via a file:// URL.
  // ---------------------------------------------------------------------------
  MEETING_START:                'meeting:start',
  MEETING_STOP:                 'meeting:stop',
  MEETING_SAVE_AUDIO:           'meeting:saveAudio',
  MEETING_LIST:                 'meeting:list',
  MEETING_GET:                  'meeting:get',
  MEETING_DELETE:               'meeting:delete',
  MEETING_RENAME:               'meeting:rename',
  MEETING_READ_AUDIO:           'meeting:readAudio',
  MEETING_LIST_NOTES:           'meeting:listNotes',
  MEETING_ADD_NOTE:             'meeting:addNote',

  // ---------------------------------------------------------------------------
  // Meeting Package Service (Cut C) — bundles PDF report + audio + notes JSON
  // + intelligence JSON + action register JSON into a single dated folder
  // under <portable root>/projects/<projectId>/meetings/packages/.
  // ---------------------------------------------------------------------------
  MEETING_PACKAGE_BUILD:        'meeting:packageBuild',

  // (channels continued below — kept grouped by engine for readability)
  MIE_EXTRACT:                  'mie:extract',
  MIE_GET_LATEST:               'mie:getLatest',
  MIE_LIST:                     'mie:list',
  MIE_ACTION_LIST:              'mie:actionList',
  MIE_ACTION_UPDATE:            'mie:actionUpdate',

  // ---------------------------------------------------------------------------
  // Document Intelligence Engine (DIE) Phase 1
  // ---------------------------------------------------------------------------
  DIE_LIST:                     'die:list',
  DIE_GET:                      'die:get',
  DIE_RESCAN_PROJECT:           'die:rescanProject',
  DIE_DUPLICATES:               'die:duplicates',
  DIE_ORPHANS:                  'die:orphans',
  DIE_WARNINGS:                 'die:warnings',
  DIE_LINK:                     'die:link',
  DIE_UNLINK:                   'die:unlink',
  DIE_LINKS_FOR_TARGET:         'die:linksForTarget',
  DIE_SUMMARY:                  'die:summary',

  // ---------------------------------------------------------------------------
  // Calendar Engine (Phase 1 internal + Phase 2 provider scaffold)
  // ---------------------------------------------------------------------------
  CAL_LIST:                     'cal:list',
  CAL_GET:                      'cal:get',
  CAL_CREATE:                   'cal:create',
  CAL_UPDATE:                   'cal:update',
  CAL_DELETE:                   'cal:delete',
  CAL_UPCOMING:                 'cal:upcoming',
  CAL_TODAY:                    'cal:today',
  CAL_OVERDUE:                  'cal:overdue',
  CAL_PROVIDER_STATE:           'cal:providerState',
  CAL_PROVIDER_CONNECT:         'cal:providerConnect',
  CAL_PROVIDER_DISCONNECT:      'cal:providerDisconnect',
  CAL_SYNC_NOW:                 'cal:syncNow',
  CAL_SYNC_DIAGNOSTICS:         'cal:syncDiagnostics',
  CAL_CONFLICTS:                'cal:conflicts',
  CAL_RESOLVE_CONFLICT:         'cal:resolveConflict',
  CAL_PULL_FROM_PROVIDER:       'cal:pullFromProvider',
  CAL_POPULATE_DEADLINES:       'cal:populateDeadlines',

  // ---------------------------------------------------------------------------
  // Mail Intelligence (Enterprise / Designer tier)
  //   Scans M365 inbox for BIM-related attachments, auto-suggests project
  //   links, and ingests confirmed files into the Document Register.
  // ---------------------------------------------------------------------------
  MAIL_STATUS:                  'mail:status',
  MAIL_CONNECT:                 'mail:connect',
  MAIL_DISCONNECT:              'mail:disconnect',
  MAIL_SCAN:                    'mail:scan',
  MAIL_ITEMS:                   'mail:items',
  MAIL_CONFIRM_LINK:            'mail:confirmLink',
  MAIL_DISMISS:                 'mail:dismiss',
  MAIL_HISTORY:                 'mail:history',

  // ---------------------------------------------------------------------------
  // Smart Ingestion Engine (SIE) Phase 1 + Contacts/Organizations.
  //   sie:parse        — paste text → draft + detections (no DB writes).
  //   sie:list / get   — review queue / single draft.
  //   sie:apply        — write approved detections through ContactService
  //                       + CalendarEngine + ProjectLogService.
  //   sie:discard      — soft-delete (status='discarded').
  //   contact:* / org:* — CRUD for the new authoritative people graph.
  // ---------------------------------------------------------------------------
  SIE_PARSE:                    'sie:parse',
  SIE_LIST_DRAFTS:              'sie:listDrafts',
  SIE_GET_DRAFT:                'sie:getDraft',
  SIE_APPLY_DRAFT:              'sie:applyDraft',
  SIE_DISCARD_DRAFT:            'sie:discardDraft',

  CONTACT_LIST_BY_PROJECT:      'contact:listByProject',
  CONTACT_LIST:                 'contact:list',
  ORG_LIST:                     'org:list',

  // ---------------------------------------------------------------------------
  // Project File Explorer (PFE) Phase 1 — read-only projection of the
  // document_register populated by FileIntegrityEngine + FolderMonitor.
  // No filesystem writes; previews flow through DocumentSource resolver.
  // ---------------------------------------------------------------------------
  PFE_TREE:                     'pfe:tree',
  PFE_NODE_META:                'pfe:nodeMeta',
  PFE_PREVIEW:                  'pfe:preview',

  // ---------------------------------------------------------------------------
  // File Integrity Layer — single chokepoint for file identity + version +
  // checksum + sheet number. PDF/DWG/Revit viewers MUST resolve files via
  // these channels rather than opening raw filesystem paths.
  // ---------------------------------------------------------------------------
  FILE_GET_BY_PATH:             'file:getByPath',
  FILE_GET:                     'file:get',
  FILE_LIST_BY_PROJECT:         'file:listByProject',
  FILE_SHEET_INDEX:             'file:sheetIndex',
  FILE_LIST_VERSIONS:           'file:listVersions',

  // ---------------------------------------------------------------------------
  // Calibration Engine — cheap startup self-test channels.
  // ---------------------------------------------------------------------------
  PING_MAIN_PROCESS:            'system:ping',

  // ---------------------------------------------------------------------------
  // Push channels (Main → Renderer)
  // ---------------------------------------------------------------------------
  PUSH_NOTIFICATION:        'push:notification',
  PUSH_SYNC_PROGRESS:       'push:syncProgress',
  PUSH_TWIN_UPDATE:         'push:twinUpdate',
  PUSH_CLASH_RESULT:        'push:clashResult',
  PUSH_AI_STREAM_DELTA:     'push:aiStreamDelta',
  PUSH_AI_STREAM_DONE:      'push:aiStreamDone',
  PUSH_ENTRA_DEVICE_CODE:   'push:entraDeviceCode',

  // ---------------------------------------------------------------------------
  // FRIS — Feature Readiness Indicator System (observability plane)
  // ---------------------------------------------------------------------------
  FRIS_STATUS:              'fris:status',
  FRIS_RECOMPUTE:           'fris:recompute',

  // ---------------------------------------------------------------------------
  // License Gate — RCDD Verification (14-day trial → verified state)
  // ---------------------------------------------------------------------------
  LICENSE_STATUS:           'license:status',
  LICENSE_VERIFY:           'license:verify',
} as const;

export type IPCChannel = (typeof IPC)[keyof typeof IPC];
